import React from 'react'

function parseOcrData(raw) {
  if (!raw) return null
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function fmtDob(val) {
  if (!val) return '—'
  const s = String(val).trim()
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (dmy) return `${dmy[1].padStart(2, '0')}/${dmy[2].padStart(2, '0')}/${dmy[3]}`
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) {
    const dd = String(parsed.getUTCDate()).padStart(2, '0')
    const mm = String(parsed.getUTCMonth() + 1).padStart(2, '0')
    const yyyy = parsed.getUTCFullYear()
    return `${dd}/${mm}/${yyyy}`
  }
  return s.slice(0, 32)
}

function cardNumber(ocr) {
  if (!ocr) return '—'
  return (
    ocr.document_card_number ||
    ocr.licence_card_number ||
    ocr.rta_number ||
    '—'
  )
}

function docTypeLabel(type) {
  if (!type) return 'ID'
  const t = String(type).toLowerCase()
  if (t.includes('licence') || t.includes('license')) return 'DL'
  if (t.includes('passport')) return 'Passport'
  return type.replace(/_/g, ' ')
}

function hasOcrContent(ocr) {
  if (!ocr || typeof ocr !== 'object') return false
  return [
    ocr.given_name,
    ocr.family_name,
    ocr.document_number,
    ocr.document_card_number,
    ocr.expiry_date,
    ocr.address_line,
  ].some((v) => v != null && String(v).trim() !== '')
}

/**
 * Read-only panel for structured licence/OCR extract retained after image purge.
 * @param {{ compact?: boolean }} props - compact: dense inline block for KYC summary areas
 */
export default function OcrExtractPanel({
  ocrData,
  title = 'ID extract (retained after image purge)',
  subtitle,
  compact = false,
}) {
  const ocr = parseOcrData(ocrData)
  if (!hasOcrContent(ocr)) return null

  if (compact) {
    const name = [ocr.given_name, ocr.family_name].filter(Boolean).join(' ')
    const docLine = [
      docTypeLabel(ocr.document_type),
      ocr.document_number,
      ocr.state_of_issue,
    ]
      .filter((v) => v != null && String(v).trim() !== '')
      .join(' · ')
    const personLine = [
      name || null,
      ocr.date_of_birth ? `DOB ${fmtDob(ocr.date_of_birth)}` : null,
      ocr.expiry_date ? `Exp ${fmtDob(ocr.expiry_date)}` : null,
    ]
      .filter(Boolean)
      .join(' · ')
    const card = cardNumber(ocr)
    const cardLine = card !== '—' ? `Card ${card}` : null

    return (
      <div className="rounded border border-indigo-100 bg-indigo-50/50 px-3 py-2 text-xs leading-relaxed text-gray-800 space-y-1">
        <div className="text-sm font-semibold text-indigo-900">{title}</div>
        {docLine ? <div className="font-medium text-gray-900 break-words">{docLine}</div> : null}
        {personLine ? <div className="break-words">{personLine}</div> : null}
        {cardLine ? <div className="text-gray-700 break-words">{cardLine}</div> : null}
        {subtitle ? (
          <div className="text-[10px] text-indigo-700/75 break-words">{subtitle}</div>
        ) : null}
      </div>
    )
  }

  const rows = [
    ['Document type', ocr.document_type || '—'],
    ['Licence / document no.', ocr.document_number || '—'],
    ['Card ID (RTA)', cardNumber(ocr)],
    ['Given name', ocr.given_name || '—'],
    ['Family name', ocr.family_name || '—'],
    ['Date of birth', fmtDob(ocr.date_of_birth)],
    ['Expiry', fmtDob(ocr.expiry_date)],
    ['State of issue', ocr.state_of_issue || '—'],
    ['Address', ocr.address_line || '—'],
    ['Suburb', ocr.address_suburb || '—'],
    ['Postcode', ocr.address_postcode || '—'],
    ['Country', ocr.country_code || '—'],
    ['OCR source', ocr.ocr_source || '—'],
    ['Captured', ocr.captured_at ? new Date(ocr.captured_at).toLocaleString() : '—'],
  ]

  return (
    <div className="mb-6 rounded-lg border border-indigo-100 bg-indigo-50/50 p-4">
      <h4 className="text-sm font-semibold text-indigo-900 mb-1">{title}</h4>
      <p className="text-[11px] text-indigo-700/80 mb-3">
        {subtitle ||
          'Structured fields from licence OCR — kept for audit when ID images are deleted after DVS.'}
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="font-medium text-gray-900 break-words">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
